import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Tree, Table, Input, Button, Space, Modal, Dropdown, App, Empty, Spin, Tag,
  Tooltip, Typography, Select, TreeSelect, Image, DatePicker, Checkbox, Segmented,
} from 'antd';
import type { Dayjs } from 'dayjs';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, FolderOutlined, FileOutlined,
  LockOutlined, MoreOutlined, FolderAddOutlined, SearchOutlined,
  CopyOutlined, ClearOutlined, PictureOutlined, DashboardOutlined,
  ThunderboltOutlined, TagOutlined,
} from '@ant-design/icons';
import type { DataNode, EventDataNode } from 'antd/es/tree';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  getSceneTree, getSceneList, getSceneLabels, getSceneGroupStats,
  createSceneDir, renameSceneTreeNode, deleteSceneTreeNode, copySceneTreeNode,
  batchDeleteScenesFromTree, clearSceneTreeNode, setTreeNodeSecurityLevel,
  setScenesSecurityLevel,
  copySceneSetContent, previewCopySceneSetContent,
  getSceneStatusList,
  getSceneTagTree, getSceneTagScenes,
  SceneTreeNode, SceneListItem, SceneGroupStat,
  CopySetContentTargetResult,
  SceneTagNode, SceneTagSceneItem, tagDisplayName,
  type StatusOption,
} from '@apis/simulation';
import { useCurrentUser } from '@/common/auth/adminAcl';
import { LockFilled, UnlockOutlined } from '@ant-design/icons';

/** 数据密级：设置/取消保密的权限点 */
const CONFIDENTIAL_MANAGE_PERM = 'data:confidential:manage';
import SceneSourceTag, { SCENE_SOURCE_FILTER_OPTIONS } from '@components/SceneSourceTag';
import ApiCodeLink from '@components/ApiCodeLink';
import SceneOverviewPanel from '@components/SceneOverviewPanel';
import FullSceneList from '@components/FullSceneList';
import { ResizableTitle, useResizableColumns } from '@components/ResizableTable';
import CreateJobModal, { MAX_SCENE_IDS_PER_JOB, PresetScene } from '@components/CreateJobModal';
import { useEnterToSearch } from '@/common/keyboard';

const { Text } = Typography;

const formatTime = (val: string | null | undefined) =>
  val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-';

const getRelativeTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const then = dayjs(iso);
  if (!then.isValid()) return '';
  const now = dayjs();
  const minutes = now.diff(then, 'minute');
  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = now.diff(then, 'hour');
  if (hours < 24) return `${hours} 小时前`;
  const days = now.diff(then, 'day');
  if (days < 30) return `${days} 天前`;
  const months = now.diff(then, 'month');
  if (months < 12) return `${months} 个月前`;
  return `${now.diff(then, 'year')} 年前`;
};

const getAllLeafIds = (node: SceneTreeNode): number[] => {
  if (node.nodeFlag === 1) return [node.id];
  if (!node.children?.length) return [];
  return node.children.flatMap(getAllLeafIds);
};

/**
 * scene_group 英文枚举 → 中文展示名。枚举来源与 xsim regression_scenarios/<scene_group>/
 * 目录对齐；未在此表内命中的取原值展示（防止漏掉新增分组）。
 */
const SCENE_GROUP_LABELS: Record<string, string> = {
  detour_problem: '绕行问题',
  change_lanes: '变道问题',
  abnormal_speed: '速度异常',
  quick_or_heavy_brake: '急刹 / 重刹',
  abnormal_braking: '异常制动',
  safety_distance: '安全距离',
  collision: '碰撞',
  parking: '泊车',
  follow: '跟车',
  cruise: '巡航',
  ring_problem: '环岛',
  other: '其他',
};

/** 与后端 sentinel 一致：scene_group 为 NULL/空串时归到「未分类」桶。 */
const UNCATEGORIZED_KEY = '__uncategorized__';

const sceneGroupDisplay = (key: string): string => {
  if (key === UNCATEGORIZED_KEY) return '未分类';
  return SCENE_GROUP_LABELS[key] ?? key;
};

/**
 * 把 name 中命中 kw 的子串包一层 <mark> 高亮。kw 已 lowercase,匹配也走 lowercase;
 * 空 kw 直接返回原文本;不命中同样返回原文本。避免在渲染路径做额外正则。
 */
const renderHighlightedName = (name: string, kw: string): React.ReactNode => {
  if (!kw) return name;
  const lower = name.toLowerCase();
  const idx = lower.indexOf(kw);
  if (idx < 0) return name;
  return (
    <>
      {name.slice(0, idx)}
      <mark style={{ background: '#fff566', padding: 0, color: 'inherit' }}>
        {name.slice(idx, idx + kw.length)}
      </mark>
      {name.slice(idx + kw.length)}
    </>
  );
};

/**
 * 树节点 key 编码：真实场景树节点直接用 number（node.id）；
 * daily 分组虚拟子节点用字符串 `group:<parentLeafId>:<groupKey>`，
 * onSelect 时反解出 parentLeafId + groupKey，走 sceneGroups 过滤查询。
 * 用字符串前缀区分是最简单的方案，避免 id 空间冲突。
 */
const GROUP_KEY_PREFIX = 'group:';
const encodeGroupKey = (parentLeafId: number, groupKey: string) =>
  `${GROUP_KEY_PREFIX}${parentLeafId}:${groupKey}`;
const decodeGroupKey = (key: React.Key): { parentLeafId: number; groupKey: string } | null => {
  if (typeof key !== 'string' || !key.startsWith(GROUP_KEY_PREFIX)) return null;
  const rest = key.slice(GROUP_KEY_PREFIX.length);
  const idx = rest.indexOf(':');
  if (idx <= 0) return null;
  const parentLeafId = Number(rest.slice(0, idx));
  const groupKey = rest.slice(idx + 1);
  if (!Number.isFinite(parentLeafId) || !groupKey) return null;
  return { parentLeafId, groupKey };
};

/**
 * 判断某节点是否位于「daily」子树内（含 daily 根节点自身与其全部后代）。
 * daily 根以 name === 'daily' 且 parentId === -1 识别。
 */
const isNodeUnderDailyTree = (
  treeData: SceneTreeNode[],
  nodeId: number | null,
): boolean => {
  if (nodeId == null) return false;
  const dailyRoot = treeData.find(n => n.name === 'daily' && n.parentId === -1);
  if (!dailyRoot) return false;
  const collect = (n: SceneTreeNode, acc: Set<number>) => {
    acc.add(n.id);
    n.children?.forEach(c => collect(c, acc));
  };
  const idSet = new Set<number>();
  collect(dailyRoot, idSet);
  return idSet.has(nodeId);
};

const findNodeById = (nodes: SceneTreeNode[], id: number): SceneTreeNode | null => {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children?.length) {
      const found = findNodeById(n.children, id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * 判断节点 ``nodeId`` 自身或其任一祖先是否被锁定。
 * 锁定语义沿祖先链向下传递（与后端 ``_is_locked_chain`` 对齐），
 * 用于前端按钮禁用，避免明知会被后端拒还点击后再报错。
 */
const isLockedChain = (treeData: SceneTreeNode[], nodeId: number): boolean => {
  // 建立 id → node 索引以避免每跳一次祖先做 O(N) 全树搜索
  const flat = new Map<number, SceneTreeNode>();
  const walk = (ns: SceneTreeNode[]) => {
    for (const n of ns) {
      flat.set(n.id, n);
      if (n.children?.length) walk(n.children);
    }
  };
  walk(treeData);
  let curId: number | undefined = nodeId;
  const seen = new Set<number>();
  while (curId !== undefined && curId !== -1 && curId !== 0 && !seen.has(curId)) {
    seen.add(curId);
    const cur = flat.get(curId);
    if (!cur) return false;
    if (cur.isLocked) return true;
    curId = cur.parentId;
  }
  return false;
};

/** 收集 root 自身 + 全部后代 id 集合（用于复制弹窗中禁用循环目标）。 */
const collectSelfAndDescendantIds = (root: SceneTreeNode): Set<number> => {
  const ids = new Set<number>([root.id]);
  const walk = (n: SceneTreeNode) => {
    n.children?.forEach(c => { ids.add(c.id); walk(c); });
  };
  walk(root);
  return ids;
};

export default function SceneManagementPage() {
  const { message, modal } = App.useApp();
  const { hasPerm } = useCurrentUser();
  const canManageSecurity = hasPerm(CONFIDENTIAL_MANAGE_PERM);
  const navigate = useNavigate();
  // URL 参数 nodeId 支持：外部页面(对比页 / 任务列表)通过 /scene-management?nodeId=xxx
  // 跳转来时,tree 加载完成后自动选中该节点、展开祖先链、切到「场景列表」视图。
  const [searchParams] = useSearchParams();

  const [treeData, setTreeData] = useState<SceneTreeNode[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  // 场景树搜索:输入框绑定 `treeSearchInput` 实时更新,但**只有** Enter /
  // 点搜索按钮 / allowClear 清除 才会把它提交到 `treeSearchValue`(真正驱动
  // 高亮 + 展开的关键词)。避免用户边打字边触发昂贵的整树 walk 与展开动作。
  /**
   * 场景集树搜索关键字（按节点 name 子串匹配，忽略大小写）。
   * 命中节点自身高亮，并把祖先链自动展开，方便定位深层节点。
   * 空串 = 无过滤（也不强制展开），与默认收起态一致。
   */
  const [treeSearchInput, setTreeSearchInput] = useState('');
  const [treeSearchValue, setTreeSearchValue] = useState('');
  /**
   * daily 集合叶子节点按 scene_group 分组统计的缓存，key=叶子 tree_node_id，
   * value=后端返回的 [{sceneGroup, count}, ...]。展开该叶子节点时懒加载 + 缓存。
   * treeData 刷新时清空，与场景数据保持一致。
   */
  const [groupStatsByLeaf, setGroupStatsByLeaf] = useState<Record<number, SceneGroupStat[]>>({});
  /** 当前用户选中的分组虚拟节点 `{parentLeafId, groupKey}`；非空时右侧列表按 scene_group 过滤。 */
  const [selectedGroupNode, setSelectedGroupNode] = useState<{ parentLeafId: number; groupKey: string } | null>(null);

  // 右侧主区域视图模式：
  //   'overview'  → 场景概览面板（默认，未选节点时也走这个）
  //   'scene-list'→ 选中节点后的场景列表
  // 用户点 Tree 节点 → 切到 scene-list；点左上角「概览」按钮 → 切回 overview
  // 并清掉 selectedNodeId，让 Tree 也回到「无选中」状态，视觉一致。
  const [viewMode, setViewMode] = useState<'overview' | 'scene-list'>('overview');

  // ── 标签检索面板（与场景集侧栏同位的第二个左侧视图） ──
  const [leftPanelMode, setLeftPanelMode] = useState<'scene-set' | 'tag'>('scene-set');
  const [tagTree, setTagTree] = useState<SceneTagNode[]>([]);
  const [tagTreeLoading, setTagTreeLoading] = useState(false);
  const [tagExpandedKeys, setTagExpandedKeys] = useState<React.Key[]>([]);
  // 标签检索支持多选：场景列表取所有选中标签（含目录，自动展开子标签）的组合。
  // checkedTagKeys = 树上的原始勾选节点(含目录,供后端做「组内并集/组间交集」)；
  // selectedTags = 展开后的叶子(仅用于 chips 展示)。
  const [checkedTagKeys, setCheckedTagKeys] = useState<React.Key[]>([]);
  const [selectedTags, setSelectedTags] = useState<SceneTagNode[]>([]);
  // 标签场景请求序号:丢弃乱序到达的过期响应
  const tagLoadSeqRef = useRef(0);
  const [tagScenes, setTagScenes] = useState<SceneTagSceneItem[]>([]);
  const [tagScenesLoading, setTagScenesLoading] = useState(false);
  const [tagPagination, setTagPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const findTagNode = (nodes: SceneTagNode[], id: number): SceneTagNode | null => {
    for (const n of nodes) {
      if (n.id === id) return n;
      const hit = findTagNode(n.children || [], id);
      if (hit) return hit;
    }
    return null;
  };

  const tagAntdData = useMemo<DataNode[]>(() => tagTree.map((n) => tagTreeNodeToDataNode(n)), [tagTree]);

  function tagTreeNodeToDataNode(n: SceneTagNode): DataNode {
    return {
      key: n.id,
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {n.nodeFlag === 1
            ? <TagOutlined style={{ color: '#3b82f6' }} />
            : <FolderOutlined style={{ color: '#faad14' }} />}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {tagDisplayName(n)}
          </span>
        </div>
      ),
      children: (n.children && n.children.length > 0) ? n.children.map(tagTreeNodeToDataNode) : undefined,
    };
  }

  const loadTagTree = useCallback(async () => {
    setTagTreeLoading(true);
    try {
      const data = await getSceneTagTree();
      setTagTree(data);
      setTagExpandedKeys(data.map((n) => n.id));
    } catch (e) {
      console.error('加载标签树失败:', e);
    } finally {
      setTagTreeLoading(false);
    }
  }, []);

  /** 按标签组合（最大节点分组：组内并集、组间交集）加载场景；tagIds 为空时直接清空列表。 */
  const loadTagScenes = useCallback(async (tagIds: number[], current = 1, pageSize = 20) => {
    // 慢链路下连续勾选会并发多个请求，用序号丢弃过期响应，避免旧结果覆盖新结果
    const seq = ++tagLoadSeqRef.current;
    if (tagIds.length === 0) {
      setTagScenes([]);
      setTagPagination({ current: 1, pageSize, total: 0 });
      return;
    }
    setTagScenesLoading(true);
    try {
      const { total, list } = await getSceneTagScenes(tagIds, current, pageSize);
      if (seq !== tagLoadSeqRef.current) return;  // 已有更新的请求，丢弃本次结果
      setTagScenes(list);
      setTagPagination({ current, pageSize, total });
    } catch (e) {
      console.error('加载标签场景失败:', e);
    } finally {
      if (seq === tagLoadSeqRef.current) setTagScenesLoading(false);
    }
  }, []);

  // 勾选目录自动展开为后代叶子（目录只是组织形式，真正有信息量的是叶子标签）。
  // 请求用**原始勾选节点**（含目录）：后端按「目录=组内并集、组间交集」计算；
  // chips 展示仍只显示叶子。
  const handleTagCheck = useCallback((keys: React.Key[]) => {
    setCheckedTagKeys(keys);
    const sourceNodes = keys
      .map((k) => findTagNode(tagTree, Number(k)))
      .filter((n): n is SceneTagNode => n !== null);
    // 目录展开:收集所有后代叶子,去重
    const leaves: SceneTagNode[] = [];
    const walk = (ns: SceneTagNode[]) => {
      for (const n of ns) {
        if (n.nodeFlag === 1) { leaves.push(n); continue; }
        if (n.children?.length) walk(n.children);
      }
    };
    walk(sourceNodes);
    const unique = [...new Map(leaves.map((l): [number, SceneTagNode] => [l.id, l])).values()];
    setSelectedTags(unique);
    setTagPagination({ current: 1, pageSize: 20, total: 0 });
    void loadTagScenes(keys.map(Number), 1);
  }, [tagTree, loadTagScenes]);

  /** 关掉一个已选标签 chip：若来自勾选的目录组则整组移除，否则仅移除该叶子。 */
  const handleTagRemove = useCallback((tagId: number) => {
    const checkedIds = checkedTagKeys.map(Number);
    // 向上找最近的已勾选祖先目录(组根)
    let groupId = tagId;
    let cursor = findTagNode(tagTree, tagId);
    while (cursor && cursor.parentId > 0) {
      if (checkedIds.includes(cursor.parentId)) { groupId = cursor.parentId; break; }
      cursor = findTagNode(tagTree, cursor.parentId);
    }
    const removeIds = new Set<number>([groupId]);
    const groupNode = findTagNode(tagTree, groupId);
    const collect = (n: SceneTagNode) => { removeIds.add(n.id); (n.children || []).forEach(collect); };
    if (groupNode) collect(groupNode);
    handleTagCheck(checkedTagKeys.filter((k) => !removeIds.has(Number(k))));
  }, [checkedTagKeys, tagTree, handleTagCheck]);

  const clearTagSelection = useCallback(() => {
    setCheckedTagKeys([]);
    setSelectedTags([]);
    void loadTagScenes([], 1);
  }, [loadTagScenes]);

  /** 勾选节点取「最大节点」：去掉被勾选祖先覆盖的后代（级联勾选时目录+子标签同时存在）。
   *  与后端 query_scenes_of_tags 的归并语义一致：目录=组内并集、组间交集。 */
  const maximalCheckedTags = useMemo(() => {
    const selSet = new Set(checkedTagKeys.map(Number));
    return checkedTagKeys
      .map((k) => findTagNode(tagTree, Number(k)))
      .filter((n): n is SceneTagNode => n !== null)
      .filter((n) => {
        let cur: SceneTagNode | null = n;
        while (cur && cur.parentId > 0) {
          if (selSet.has(cur.parentId)) return false;
          cur = findTagNode(tagTree, cur.parentId);
        }
        return true;
      });
  }, [checkedTagKeys, tagTree]);

  /** 收集节点下全部叶子标签(目录组用于 chips 展示叶子名,不显示目录名)。 */
  const collectLeavesOf = (n: SceneTagNode): SceneTagNode[] => {
    if (n.nodeFlag === 1) return [n];
    return (n.children || []).flatMap(collectLeavesOf);
  };

  useEffect(() => { void loadTagTree(); }, [loadTagTree]);

  const TAG_SCENE_COLUMNS = useMemo(() => [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: '场景名称',
      dataIndex: 'name',
      ellipsis: true,
      render: (v: string | null | undefined, r: SceneTagSceneItem) => (
        v ? <Link to={`/scene-annotate?sceneId=${r.id}`}>{v}</Link> : '-'
      ),
    },
    { title: '标签', dataIndex: 'label', width: 130, render: (v: string | null | undefined) => v || '-' },
    { title: '来源', dataIndex: 'source', width: 110, render: (v: string | null | undefined) => <SceneSourceTag source={v} /> },
    { title: '状态', dataIndex: 'statusName', width: 100, render: (v: string | null | undefined) => v || '-' },
    { title: '创建人', dataIndex: 'creator', width: 120, render: (v: string | null | undefined) => v || '-' },
    { title: '创建时间', dataIndex: 'createTime', width: 160, render: (v: string | null | undefined) => formatTime(v) },
  ], []);

  const [scenes, setScenes] = useState<SceneListItem[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  // 场景评论列已下线:场景管理页评论展示是只读且无写入口(SceneManagement 只透传
  // sceneId,DB 支持但前端没入口),真正有价值的评论入口在 JobTaskList/DailySimulation
  // 的"任务维度评论"(带 jobId/taskId);这里去掉列避免功能半残误导用户。
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  // 从详情页返回时按 URL ?page= 恢复到原分页(nodeId 自动选中后消费一次即清)。
  const pendingRestorePageRef = useRef<number | null>(null);

  // 场景列表滚动容器 ref:传给 Table sticky.getContainer,让表头在容器内滚时钉在顶部。
  const sceneTableScrollRef = useRef<HTMLDivElement | null>(null);

  // 5 个过滤项均支持多值（Select tags / multiple）+ 创建时间 RangePicker
  const [filterIds, setFilterIds] = useState<string[]>([]);
  const [filterNames, setFilterNames] = useState<string[]>([]);
  const [filterJiras, setFilterJiras] = useState<string[]>([]);
  const [filterLabels, setFilterLabels] = useState<string[]>([]);
  const [filterSources, setFilterSources] = useState<string[]>([]);
  const [filterRegions, setFilterRegions] = useState<string[]>([]);
  /** RangePicker 受控值；两端任一缺失视作未选（避免半开区间错位）。 */
  const [filterCreateTimeRange, setFilterCreateTimeRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [labelOptions, setLabelOptions] = useState<string[]>([]);
  const [labelLoading, setLabelLoading] = useState(false);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createNodeFlag, setCreateNodeFlag] = useState(0);
  const [createParentId, setCreateParentId] = useState<number>(-1);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // 跨页累积选中的场景对象（id+name），传给 CreateJobModal 免去 id→name 反查接口
  const [selectedScenes, setSelectedScenes] = useState<PresetScene[]>([]);
  /**
   * 场景树 checkbox 勾选的 key 集合（含真实节点 id: number 与分组虚拟节点 key: string）。
   * 通过 splitCheckedKeys 拆成 realNodeIds / groupKeys 两类使用：
   *  - realNodeIds → 走「按场景集合」发起 job（sceneSetIds，无 100 上限）
   *  - groupKeys   → 拉对应场景 id → 走「按场景」发起 job（sceneIds，受 100 上限）
   *  - 两类同时有 → 先合并成 sceneIds 一起发（都受 100 上限），提示上限
   */
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);

  /**
   * 把勾选 keys 编码成 CreateJobModal 的 presetSceneSetIds 参数：
   * - number 类型的 tree_node_id 直接透传
   * - 字符串 `group:<parentLeafId>:<groupKey>`（daily 分组虚拟节点）
   *   → 后端可解析格式 `L<parentLeafId>G:<groupKey>` 加入串（见 job_service._parse_scene_set_tokens）
   *
   * 两类混选时后端一并处理，不受 sceneIds 100 上限约束（走「按场景集」链路）。
   */
  const encodedPresetSceneSetIds = useMemo<(number | string)[]>(() => {
    const out: (number | string)[] = [];
    for (const k of checkedKeys) {
      if (typeof k === 'number') {
        out.push(k);
      } else {
        const d = decodeGroupKey(k);
        if (d) out.push(`L${d.parentLeafId}G:${d.groupKey}`);
      }
    }
    return out;
  }, [checkedKeys]);

  /** 分组虚拟节点在弹窗顶部展示用的中文名映射 `{ "L91G:detour_problem": "daily / 绕行问题" }`。 */
  const presetSceneSetGroupLabels = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const k of checkedKeys) {
      if (typeof k !== 'string') continue;
      const d = decodeGroupKey(k);
      if (!d) continue;
      const parent = findNodeById(treeData, d.parentLeafId);
      const parentName = parent?.name || `#${d.parentLeafId}`;
      map[`L${d.parentLeafId}G:${d.groupKey}`] = `${parentName} / ${sceneGroupDisplay(d.groupKey)}`;
    }
    return map;
  }, [checkedKeys, treeData]);
  const clearSelection = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedScenes([]);
  }, []);
  // 「发起任务」弹窗：按勾选的场景 id 发起 job（共用 JobList 的创建弹窗）
  const [jobModalOpen, setJobModalOpen] = useState(false);

  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameId, setRenameId] = useState(0);
  const [renameName, setRenameName] = useState('');

  // 场景集合复制（浅拷贝节点 + scene_tree_node 关联，叶子和目录均支持）
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySourceId, setCopySourceId] = useState(0);
  const [copyTargetParentId, setCopyTargetParentId] = useState<number>(-1);
  const [copyNewName, setCopyNewName] = useState('');
  const [copySubmitting, setCopySubmitting] = useState(false);

  // 跨集复制内容（同一 case 同时属于多个集）——「复制场景内容到…」独立 Modal
  const [copyContentModalOpen, setCopyContentModalOpen] = useState(false);
  const [copyContentSourceId, setCopyContentSourceId] = useState(0);
  const [copyContentSourceName, setCopyContentSourceName] = useState('');
  const [copyContentTargetIds, setCopyContentTargetIds] = useState<number[]>([]);
  const [copyContentSubmitting, setCopyContentSubmitting] = useState(false);
  // 预览"预计复制 N 个"；null 表示未预览过；-1 表示预览中
  const [copyContentPreviewTotal, setCopyContentPreviewTotal] = useState<number | null>(null);
  // 预览请求版本号 —— 每次发新请求 +1；返回时对比最新版本，只有匹配才写入 state，
  // 防止用户快速切 filter 时旧请求返回覆盖新的正确结果（竞态修复）。
  const copyContentPreviewReqIdRef = useRef(0);
  // 服务端返回的每个 target 结果，成功后展示在 Modal 底部
  const [copyContentResult, setCopyContentResult] = useState<{
    sourceTotal: number; targets: CopySetContentTargetResult[];
  } | null>(null);
  // filter 组（本次只暴露 status 和 sceneGroups，其他后续再加）
  const [copyContentStatusFilter, setCopyContentStatusFilter] = useState<number[]>([]);

  // 场景状态下拉从 /scene/status 拉(SceneStatusEnum),避免硬编码漏改。
  // 复制内容不给复制质检中(id=5,进行中产物未定型);其它稳态都放开。
  const [sceneStatusOptions, setSceneStatusOptions] = useState<StatusOption[]>([]);
  useEffect(() => {
    getSceneStatusList().then(setSceneStatusOptions).catch(() => {});
  }, []);
  const copyContentStatusOptions = useMemo(
    () => sceneStatusOptions
      .filter((o) => o.value !== 5)
      .map((o) => ({ label: `状态：${o.label}`, value: o.value })),
    [sceneStatusOptions],
  );

  // 清空场景集（高危操作，需输入集合名称二次确认）
  const [clearModalOpen, setClearModalOpen] = useState(false);
  const [clearConfirmText, setClearConfirmText] = useState('');
  const [clearSubmitting, setClearSubmitting] = useState(false);
  const [clearTargetNode, setClearTargetNode] = useState<{ id: number; name: string; total: number } | null>(null);

  const loadTree = useCallback(async () => {
    setTreeLoading(true);
    try {
      const data = await getSceneTree();
      setTreeData(data);
      // 树刷新时清空分组统计缓存：树可能新增/删除节点，缓存里的 leafId 可能已失效。
      setGroupStatsByLeaf({});
      // 默认所有场景集合收起（不再自动展开根目录）；用户手动展开或用搜索命中会自动展开祖先链。
    } finally {
      setTreeLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  type SceneFilterOverrides = {
    ids?: string[];
    names?: string[];
    jiras?: string[];
    labels?: string[];
    sources?: string[];
    regions?: string[];
    /** ``null`` 表示用户主动清空时间范围（区别于 ``undefined`` 透传 state）。 */
    createTimeRange?: [Dayjs, Dayjs] | null;
  } | null;

  /** 把 string[] tag 列表拼成后端期望的 csv（自动去空、去重）。 */
  const toCsv = (tags: string[] | undefined): string | undefined => {
    if (!tags?.length) return undefined;
    const cleaned = Array.from(new Set(tags.map(t => t.trim()).filter(Boolean)));
    return cleaned.length ? cleaned.join(',') : undefined;
  };

  /** Dayjs → 后端 ISO 8601（与 scenario / scene-tree GET Query 的 datetime 解析对齐）。 */
  const toIso = (d: Dayjs | null | undefined): string | undefined => {
    if (!d || !d.isValid()) return undefined;
    return d.format('YYYY-MM-DDTHH:mm:ss');
  };

  const loadScenes = useCallback(async (
    nodeId: number,
    current = 1,
    pageSize = 20,
    filterOverrides: SceneFilterOverrides = null,
    /** 选中的分组虚拟节点信息；非空时按此过滤 scene_group（左侧树 daily 分组子节点点击）。 */
    groupCtx: { parentLeafId: number; groupKey: string } | null = null,
  ) => {
    // 分组虚拟节点：parentIds 用真实 leaf id、加 sceneGroups 过滤参数
    const effectiveNodeId = groupCtx ? groupCtx.parentLeafId : nodeId;
    const node = findNodeById(treeData, effectiveNodeId);
    if (!node) return;
    const leafIds = getAllLeafIds(node);
    if (!leafIds.length) {
      setScenes([]);
      setPagination(p => ({ ...p, total: 0 }));
      return;
    }
    const ids = filterOverrides ? filterOverrides.ids : filterIds;
    const names = filterOverrides ? filterOverrides.names : filterNames;
    const jiras = filterOverrides ? filterOverrides.jiras : filterJiras;
    const labels = filterOverrides ? filterOverrides.labels : filterLabels;
    const sources = filterOverrides ? filterOverrides.sources : filterSources;
    const regions = filterOverrides ? filterOverrides.regions : filterRegions;
    // 用 'createTimeRange' in overrides 判断，区分 ``null``（显式清空）vs 未传
    const createTimeRange = filterOverrides && 'createTimeRange' in filterOverrides
      ? filterOverrides.createTimeRange
      : filterCreateTimeRange;
    const sceneIdsCsv = toCsv(ids);
    const sceneNamesCsv = toCsv(names);
    const jirasCsv = toCsv(jiras);
    const labelsCsv = toCsv(labels);
    const sourcesCsv = toCsv(sources);
    const regionsCsv = toCsv(regions);
    const ctsIso = createTimeRange ? toIso(createTimeRange[0]) : undefined;
    const cteIso = createTimeRange ? toIso(createTimeRange[1]) : undefined;
    setTableLoading(true);
    try {
      const resp = await getSceneList({
        parentIds: leafIds.join(','),
        current,
        pageSize,
        ...(sceneIdsCsv ? { sceneIds: sceneIdsCsv } : {}),
        ...(sceneNamesCsv ? { sceneNames: sceneNamesCsv } : {}),
        ...(jirasCsv ? { jiraIssueCodes: jirasCsv } : {}),
        ...(labelsCsv ? { labels: labelsCsv } : {}),
        ...(sourcesCsv ? { sources: sourcesCsv } : {}),
        ...(regionsCsv ? { regions: regionsCsv } : {}),
        ...(groupCtx ? { sceneGroups: groupCtx.groupKey } : {}),
        ...(ctsIso ? { createTimeStart: ctsIso } : {}),
        ...(cteIso ? { createTimeEnd: cteIso } : {}),
      });
      setScenes(resp.scenes);
      setPagination({
        current: resp.page?.current ?? current,
        pageSize: resp.page?.pageSize ?? pageSize,
        total: resp.page?.totalElements ?? resp.total,
      });
    } finally {
      setTableLoading(false);
    }
    // filterRegions 必须在 deps 里,否则 useCallback 闭包锁死首次的空数组,
    // 点「查询」时 loadScenes(overrides=null) 走的还是旧 filterRegions → 后端
    // 收不到 regions 参数,筛选看起来"无效"。
  }, [treeData, filterIds, filterNames, filterJiras, filterLabels, filterSources, filterRegions, filterCreateTimeRange]);

  /** 加载 label 下拉（后端 Redis 缓存 3h） */
  const loadLabelOptions = useCallback(async () => {
    setLabelLoading(true);
    try {
      const list = await getSceneLabels();
      setLabelOptions(list);
    } finally {
      setLabelLoading(false);
    }
  }, []);

  useEffect(() => { loadLabelOptions(); }, [loadLabelOptions]);

  useEffect(() => { loadTree(); }, [loadTree]);

  // 外部跳入自动选中: /scene-management?nodeId=53 → tree 加载后自动 select 53 + 展开祖先链
  useEffect(() => {
    const raw = searchParams.get('nodeId');
    if (!raw || treeData.length === 0) return;
    const targetId = Number(raw);
    if (!Number.isFinite(targetId) || targetId <= 0) return;
    // 从根路径收集所有祖先 id 用于展开;找不到目标节点静默(URL 过期/权限过滤后不可见 也不报错)
    const ancestors: number[] = [];
    const walk = (nodes: SceneTreeNode[], path: number[]): boolean => {
      for (const n of nodes) {
        if (n.id === targetId) { ancestors.push(...path); return true; }
        if (n.children?.length && walk(n.children, [...path, n.id])) return true;
      }
      return false;
    };
    if (walk(treeData, [])) {
      setExpandedKeys((prev) => Array.from(new Set([...prev, ...ancestors, targetId])));
      // 记住 URL ?page=,供下面 loadScenes effect 首次加载时恢复到原分页。
      const pageRaw = searchParams.get('page');
      const pageNum = pageRaw ? Number(pageRaw) : NaN;
      pendingRestorePageRef.current = Number.isFinite(pageNum) && pageNum > 0 ? pageNum : null;
      setSelectedNodeId(targetId);
      setSelectedGroupNode(null);
      setViewMode('scene-list');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData, searchParams]);

  useEffect(() => {
    if (selectedNodeId !== null && treeData.length) {
      // 有待恢复分页(从详情页返回)则用它，否则回到第 1 页；消费后清空。
      const restorePage = pendingRestorePageRef.current ?? 1;
      pendingRestorePageRef.current = null;
      loadScenes(selectedNodeId, restorePage, pagination.pageSize, null, selectedGroupNode);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId, treeData, selectedGroupNode]);

  /**
   * 展开 daily 集合叶子节点时，异步拉取该叶子下的 scene_group 分组统计，
   * 用于动态生成虚拟子节点。已缓存的不重复请求。treeData 刷新时缓存自动失效。
   */
  const ensureGroupStats = useCallback(async (leafId: number) => {
    if (groupStatsByLeaf[leafId] !== undefined) return;
    try {
      const stats = await getSceneGroupStats([leafId]);
      setGroupStatsByLeaf(prev => ({ ...prev, [leafId]: stats }));
    } catch {
      // 拉不到时写空数组占位，避免反复重试；下次刷新 tree 会重置。
      setGroupStatsByLeaf(prev => ({ ...prev, [leafId]: [] }));
    }
  }, [groupStatsByLeaf]);

  const handleSelect = (_: React.Key[], info: { node: EventDataNode<DataNode> }) => {
    const key = info.node.key;
    // 分组虚拟节点：反解出 parentLeafId + groupKey，走 sceneGroups 过滤
    const decoded = decodeGroupKey(key);
    if (decoded) {
      setSelectedNodeId(decoded.parentLeafId);
      setSelectedGroupNode(decoded);
    } else {
      setSelectedNodeId(key as number);
      setSelectedGroupNode(null);
    }
    // 选中任意节点 → 自动切换到「场景列表」视图（概览态被覆盖）
    setViewMode('scene-list');
    setFilterIds([]);
    setFilterNames([]);
    setFilterJiras([]);
    setFilterLabels([]);
    setFilterSources([]);
    setFilterRegions([]);
    setFilterCreateTimeRange(null);
    clearSelection();
  };

  /** Tree 展开：daily 子树的叶子节点展开时懒加载 scene_group 分组统计。 */
  const handleTreeExpand = (
    keys: React.Key[],
    info: { expanded: boolean; node: EventDataNode<DataNode> },
  ) => {
    setExpandedKeys(keys);
    if (info.expanded) {
      const nid = info.node.key;
      if (typeof nid === 'number' && isNodeUnderDailyTree(treeData, nid)) {
        const node = findNodeById(treeData, nid);
        if (node && node.nodeFlag === 1) ensureGroupStats(nid);
      }
    }
  };

  /** 「概览」按钮：清空 Tree 选中态 + 右侧切回概览面板。 */
  const handleSwitchToOverview = () => {
    setSelectedNodeId(null);
    setSelectedGroupNode(null);
    setViewMode('overview');
    clearSelection();
  };

  /**
   * 展开所有目录节点(叶子/分组虚拟节点不算)。
   * daily 集合叶子被视为可展开(懒加载 scene_group 分组虚拟子节点),一并展开;
   * 展开后同时对 daily 叶子触发 ensureGroupStats,保证虚拟子节点能显示出来。
   */
  const handleExpandAll = () => {
    const keys: React.Key[] = [];
    const dailyLeafIds: number[] = [];
    const walk = (nodes: SceneTreeNode[], insideDaily: boolean) => {
      for (const n of nodes) {
        const nowInsideDaily = insideDaily || n.id === dailyRootId;
        if (n.nodeFlag === 0) {
          keys.push(n.id);
        } else if (nowInsideDaily && n.nodeFlag === 1) {
          keys.push(n.id);
          dailyLeafIds.push(n.id);
        }
        if (n.children?.length) walk(n.children, nowInsideDaily);
      }
    };
    walk(treeData, false);
    setExpandedKeys(keys);
    dailyLeafIds.forEach(id => ensureGroupStats(id));
  };

  /** 关闭所有集合:清空 expandedKeys。 */
  const handleCollapseAll = () => {
    setExpandedKeys([]);
  };

  const handleSearch = () => {
    if (selectedNodeId !== null) {
      loadScenes(selectedNodeId, 1, pagination.pageSize, null, selectedGroupNode);
    }
  };

  const enterToSearch = useEnterToSearch(handleSearch);

  const handleBatchDelete = () => {
    if (!selectedRowKeys.length || selectedNodeId === null) return;
    modal.confirm({
      title: '确认移除',
      content: `确定要从当前场景集中移除选中的 ${selectedRowKeys.length} 个场景吗？`,
      okType: 'danger',
      onOk: async () => {
        const result = await batchDeleteScenesFromTree(selectedNodeId, selectedRowKeys as number[]);
        message.success(`已移除 ${result.deleted} 个场景`);
        clearSelection();
        loadScenes(selectedNodeId, pagination.current, pagination.pageSize);
      },
    });
  };

  const handleClearScenes = () => {
    if (selectedNodeId === null || !selectedNode) return;
    setClearTargetNode({ id: selectedNodeId, name: selectedNode.name, total: pagination.total });
    setClearConfirmText('');
    setClearModalOpen(true);
  };

  const submitClearScenes = async () => {
    if (!clearTargetNode) return;
    if (clearConfirmText.trim() !== clearTargetNode.name) {
      message.warning('输入的名称与场景集名称不一致');
      return;
    }
    setClearSubmitting(true);
    try {
      const result = await clearSceneTreeNode(clearTargetNode.id);
      message.success(`已清空 ${result.deleted} 个场景关联`);
      setClearModalOpen(false);
      clearSelection();
      if (selectedNodeId !== null) {
        loadScenes(selectedNodeId, 1, pagination.pageSize);
      }
    } finally {
      setClearSubmitting(false);
    }
  };

  const handleCreateDir = async () => {
    if (!createName.trim()) { message.warning('请输入名称'); return; }
    try {
      await createSceneDir({ parentId: createParentId, name: createName.trim(), nodeFlag: createNodeFlag });
      message.success('创建成功');
      setCreateModalOpen(false);
      setCreateName('');
      await loadTree();
    } catch { /* interceptor handles */ }
  };

  const handleRename = async () => {
    if (!renameName.trim()) { message.warning('请输入名称'); return; }
    try {
      await renameSceneTreeNode(renameId, renameName.trim());
      message.success('重命名成功');
      setRenameModalOpen(false);
      await loadTree();
    } catch { /* interceptor handles */ }
  };

  const handleDelete = (nodeId: number, nodeName: string) => {
    modal.confirm({
      title: '确认删除',
      content: `确定要删除「${nodeName}」及其所有子节点吗？此操作不可撤销。`,
      okType: 'danger',
      onOk: async () => {
        await deleteSceneTreeNode(nodeId);
        message.success('删除成功');
        if (selectedNodeId === nodeId) setSelectedNodeId(null);
        await loadTree();
      },
    });
  };

  const handleToggleNodeSecurity = (node: SceneTreeNode) => {
    const confidential = (node.securityLevel ?? 0) > 0;
    const nextLevel = confidential ? 0 : 1;
    modal.confirm({
      title: confidential ? '取消保密' : '设为保密',
      content: confidential
        ? `确定取消「${node.name}」的保密？取消后所有人可见（含子树全部场景）。`
        : `确定将「${node.name}」设为保密？仅有查看权限的角色可见（含子树全部场景）。`,
      onOk: async () => {
        const r = await setTreeNodeSecurityLevel(node.id, nextLevel);
        message.success(
          confidential
            ? `已取消保密（影响 ${r.stampedScenes} 个场景）`
            : `已设为保密（影响 ${r.stampedScenes} 个场景）`,
        );
        await loadTree();
      },
    });
  };

  /**
   * 单个场景的密级 toggle（右侧场景列表「...」菜单入口）。
   * 与集合级 handleToggleNodeSecurity 的差异：只影响单条 scene_inspection.security_level，
   * 不动 scene_tree 节点。仅对持有 data:confidential:manage 的用户暴露入口。
   */
  const handleToggleSceneSecurity = (scene: SceneListItem) => {
    const confidential = (scene.securityLevel ?? 0) > 0;
    const nextLevel = confidential ? 0 : 1;
    modal.confirm({
      title: confidential ? '取消保密' : '设为保密',
      content: confidential
        ? `确定取消场景「${scene.name}」的保密？取消后所有人可见。`
        : `确定将场景「${scene.name}」设为保密？仅有查看权限的角色可见。`,
      onOk: async () => {
        await setScenesSecurityLevel([scene.id], nextLevel);
        message.success(confidential ? '已取消保密' : '已设为保密');
        // 就地刷新当前页；不动集合树（未影响 scene_tree.security_level）
        if (selectedNodeId != null) {
          await loadScenes(selectedNodeId, pagination.current, pagination.pageSize);
        }
      },
    });
  };

  const openCreateModal = (parentId: number, nodeFlag: number) => {
    setCreateParentId(parentId);
    setCreateNodeFlag(nodeFlag);
    setCreateName('');
    setCreateModalOpen(true);
  };

  const openRenameModal = (id: number, name: string) => {
    setRenameId(id);
    setRenameName(name);
    setRenameModalOpen(true);
  };

  const openCopyModal = (id: number, name: string) => {
    setCopySourceId(id);
    setCopyNewName(`${name} 副本`);
    setCopyTargetParentId(-1);
    setCopyModalOpen(true);
  };

  /** 打开「复制场景内容到…」Modal —— 与 openCopyModal(浅拷贝节点结构) 分开，
   * 语义是「同一 case 同时挂到多个场景集」的多对多关联。 */
  const openCopyContentModal = (id: number, name: string) => {
    setCopyContentSourceId(id);
    setCopyContentSourceName(name);
    setCopyContentTargetIds([]);
    setCopyContentStatusFilter([]);
    setCopyContentPreviewTotal(null);
    setCopyContentResult(null);
    setCopyContentModalOpen(true);
  };

  /** 预览「预计复制 N 个」——每次 filter 变化或打开 Modal 时调一下。
   *  用 reqId 版本号防竞态：只有最新请求的响应能写入 state,旧请求返回时静默丢弃。 */
  const handlePreviewCopyContent = useCallback(async () => {
    if (!copyContentSourceId) return;
    const myReqId = ++copyContentPreviewReqIdRef.current;
    setCopyContentPreviewTotal(-1);
    try {
      const filter = copyContentStatusFilter.length > 0
        ? { status: copyContentStatusFilter } : undefined;
      const res = await previewCopySceneSetContent({
        sourceNodeId: copyContentSourceId,
        filter,
      });
      // 只写入最新请求的结果 —— 旧请求返回时已被更新的 reqId 覆盖,直接丢弃。
      if (myReqId === copyContentPreviewReqIdRef.current) {
        setCopyContentPreviewTotal(res.sourceTotal);
      }
    } catch {
      if (myReqId === copyContentPreviewReqIdRef.current) {
        setCopyContentPreviewTotal(null);
      }
    }
  }, [copyContentSourceId, copyContentStatusFilter]);

  useEffect(() => {
    if (copyContentModalOpen) handlePreviewCopyContent();
  }, [copyContentModalOpen, handlePreviewCopyContent]);

  const handleCopyContent = async () => {
    if (!copyContentSourceId) return;
    if (copyContentTargetIds.length === 0) {
      message.warning('请至少选择一个目标场景集');
      return;
    }
    if (copyContentTargetIds.includes(copyContentSourceId)) {
      message.warning('目标不能是源自身');
      return;
    }
    setCopyContentSubmitting(true);
    try {
      const filter = copyContentStatusFilter.length > 0
        ? { status: copyContentStatusFilter } : undefined;
      const res = await copySceneSetContent({
        sourceNodeId: copyContentSourceId,
        targetNodeIds: copyContentTargetIds,
        filter,
      });
      setCopyContentResult({ sourceTotal: res.sourceTotal, targets: res.targets });
      const totalAdded = res.targets.reduce((a, t) => a + t.added, 0);
      const totalSkipped = res.targets.reduce((a, t) => a + t.skipped, 0);
      message.success(
        `复制完成：源共 ${res.sourceTotal} 个 case，本次新增 ${totalAdded} 条关联`
        + (totalSkipped > 0 ? `，跳过已存在 ${totalSkipped} 条` : ''),
      );
      // 刷新树以更新场景数（现在树没显示计数，先只刷 —— 后续加计数徽标时天然生效）
      const data = await getSceneTree();
      setTreeData(data);
    } catch (err) {
      // 后端异常已由 axios 拦截器 toast，本 catch 只做 UX 兜底
      // eslint-disable-next-line no-console
      console.error('复制场景内容失败', err);
    } finally {
      setCopyContentSubmitting(false);
    }
  };

  /** 目标 TreeSelect：只允许叶子（node_flag=1），目录不可选。
   *  排除源节点自身（避免误勾）。搜索复用 antd TreeSelect 内建。 */
  const copyContentTargetTreeData = useMemo(() => {
    const walk = (nodes: SceneTreeNode[]): DataNode[] => nodes.map(n => ({
      key: n.id,
      value: n.id,
      title: n.name,
      // 目录不可选 + 排除源自身
      selectable: n.nodeFlag === 1 && n.id !== copyContentSourceId,
      disabled: n.nodeFlag !== 1 || n.id === copyContentSourceId,
      children: n.children && n.children.length > 0 ? walk(n.children) : undefined,
    }));
    return walk(treeData);
  }, [treeData, copyContentSourceId]);

  const handleCopy = async () => {
    const trimmed = copyNewName.trim();
    if (!trimmed) {
      message.warning('请输入新名称');
      return;
    }
    if (trimmed.includes('/')) {
      message.warning('名称不允许包含 /');
      return;
    }
    setCopySubmitting(true);
    try {
      const result = await copySceneTreeNode({
        sourceNodeId: copySourceId,
        targetParentId: copyTargetParentId,
        newName: trimmed,
      });
      message.success(
        `复制成功：新增 ${result.copiedNodes} 个节点 / ${result.copiedSceneRefs} 条场景关联`,
      );
      setCopyModalOpen(false);
      await loadTree();
    } finally {
      setCopySubmitting(false);
    }
  };

  /**
   * 构造复制弹窗的 TreeSelect 数据：
   * - 叶子节点（场景集）整个不显示（不能作为父节点）
   * - 源节点自身和后代禁用（防环）
   * - 始终在最前注入「(根目录)」选项 (value=-1)
   */
  const copyTargetTreeData = useMemo(() => {
    if (!copyModalOpen || !copySourceId) return [];
    const sourceNode = findNodeById(treeData, copySourceId);
    const forbiddenIds = sourceNode ? collectSelfAndDescendantIds(sourceNode) : new Set<number>();
    const buildOptions = (nodes: SceneTreeNode[]): any[] =>
      nodes
        .filter(n => n.nodeFlag === 0) // 叶子整个不可作为父节点
        .map(n => ({
          value: n.id,
          title: n.name,
          disabled: forbiddenIds.has(n.id),
          children: n.children?.length ? buildOptions(n.children) : undefined,
        }));
    return [
      { value: -1, title: '(根目录)', children: buildOptions(treeData) },
    ];
  }, [copyModalOpen, copySourceId, treeData]);

  const selectedNode = selectedNodeId !== null ? findNodeById(treeData, selectedNodeId) : null;
  const isLeafNode = selectedNode?.nodeFlag === 1;
  const selectedLocked = useMemo(
    () => (selectedNodeId !== null ? isLockedChain(treeData, selectedNodeId) : false),
    [treeData, selectedNodeId],
  );

  // 提前算 daily 根 id，避免 convert 递归里每次都 find 一遍
  const dailyRootId = useMemo(
     () => treeData.find(n => n.name === 'daily' && n.parentId === -1)?.id ?? null,
    [treeData],
  );

  /**
   * 树搜索：**不裁剪**树,只做高亮 + 命中路径祖先链自动展开。
   * 裁剪式过滤的问题:命中一个父目录后其子孙被裁掉,用户手动展开只能看到空节点,
   * 反而更难找;antd 官方 demo 也是这种"高亮 + 展开"的做法。
   * 关键字规范化(trim + lowercase)集中在这里,下面高亮与祖先链收集共用。
   */
  const treeSearchKw = useMemo(
    () => treeSearchValue.trim().toLowerCase(),
    [treeSearchValue],
  );

  /**
   * 搜索命中后:
   * - 关键字非空 → 展开树**替换**成"命中节点的祖先链",未命中的集合自动收起,
   *   让用户视野聚焦在命中路径上;
   * - 关键字空 → 不动 expandedKeys(保留用户当前手动展开态)。
   *
   * 之前是 append 模式(命中路径展开但保留旧展开),导致清关键字后未命中集合
   * 仍展开一屏、命中集合藏在中间。改成"搜索时收起未命中"符合"我在找东西"
   * 的场景需求;清空搜索后用户可以自己 `...` → 展开所有 / 关闭所有 兜底。
   */
  useEffect(() => {
    if (!treeSearchKw) return;
    const collectAncestors = (
      nodes: SceneTreeNode[],
      trail: number[],
      acc: Set<number>,
    ) => {
      for (const n of nodes) {
        const nextTrail = [...trail, n.id];
        if (n.name.toLowerCase().includes(treeSearchKw)) {
          trail.forEach(id => acc.add(id));
        }
        if (n.children?.length) collectAncestors(n.children, nextTrail, acc);
      }
    };
    const ancestors = new Set<number>();
    collectAncestors(treeData, [], ancestors);
    // 直接替换而非合并,收起所有未命中集合;命中为空时也走这条 → 全部收起,
    // 明确"没找到"的视觉反馈,而不是留着旧展开态让人以为"没搜索"。
    setExpandedKeys(Array.from(ancestors));
  }, [treeSearchKw, treeData]);

  const antTreeData = useMemo(() => {
    const convert = (nodes: SceneTreeNode[], parentLocked: boolean, insideDaily: boolean): DataNode[] =>
      nodes.map((n) => {
        // 锁定语义沿祖先链向下传递：祖先锁了，自己也按锁定 UI 表现，
        // 避免「自己未 locked 却看似可写、点了后端拒」的体验落差。
        const effectiveLocked = parentLocked || !!n.isLocked;
        const lockedTip = '该节点（或其祖先目录）已被锁定，禁止删/改/加场景';
        const nowInsideDaily = insideDaily || n.id === dailyRootId;

        // daily 集合叶子节点：把 scene_group 统计结果渲染成虚拟子节点。
        // 未加载时不给 children（Tree 会用 loadData 或者展开时通过 handleTreeExpand 触发加载）。
        let groupChildren: DataNode[] | undefined;
        if (nowInsideDaily && n.nodeFlag === 1) {
          const stats = groupStatsByLeaf[n.id];
          if (stats && stats.length) {
            groupChildren = stats.map((g) => ({
              key: encodeGroupKey(n.id, g.sceneGroup),
              title: (
                <span>
                  <FolderOutlined style={{ marginRight: 4, color: '#87c1ff' }} />
                  {sceneGroupDisplay(g.sceneGroup)}
                </span>
              ),
              isLeaf: true,
              selectable: true,
              // 分组虚拟节点参与 checkbox；勾选后走 sceneIds 语义（受 100 上限）。
              // 与真实节点混选时前端把两者合并为 sceneIds 一起发起。
              checkable: true,
            }));
          }
        }

        // daily 集合叶子：无论是否已加载统计，都当"可展开"（isLeaf=false），
        // 展开时通过 handleTreeExpand 懒加载 scene_group 统计。
        const isLeafForTree = nowInsideDaily && n.nodeFlag === 1 ? false : n.nodeFlag === 1;

        return {
          key: n.id,
          title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 6 }}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                {n.nodeFlag === 1
                  ? <FileOutlined style={{ marginRight: 4, color: '#999' }} />
                  : <FolderOutlined style={{ marginRight: 4, color: '#faad14' }} />}
                {renderHighlightedName(n.name, treeSearchKw)}
                {effectiveLocked && (
                  <Tooltip title={lockedTip}>
                    <LockOutlined
                      style={{ marginLeft: 6, color: '#fa8c16', fontSize: 12 }}
                    />
                  </Tooltip>
                )}
                {(n.securityLevel ?? 0) > 0 && (
                  <Tooltip title="保密：仅具备查看权限的角色可见">
                    <LockFilled
                      style={{ marginLeft: 6, color: '#cf1322', fontSize: 12 }}
                    />
                  </Tooltip>
                )}
              </span>
              {/*
               * 创建人 / 创建时间不再在树节点上渲染：
               * 每行一个色块视觉很拥挤，且只在 hover 才出完整信息，价值低。
               * 选中节点后由右侧标题栏统一展示（见 selectedNode meta 区域）。
               */}
              <Dropdown
                trigger={['click']}
                disabled={effectiveLocked}
                menu={{
                  items: [
                    ...(n.nodeFlag === 0 ? [
                      { key: 'add-dir', icon: <FolderAddOutlined />, label: '新建子目录', onClick: () => openCreateModal(n.id, 0) },
                      { key: 'add-leaf', icon: <PlusOutlined />, label: '新建场景集', onClick: () => openCreateModal(n.id, 1) },
                      { type: 'divider' as const },
                    ] : []),
                    { key: 'rename', icon: <EditOutlined />, label: '重命名', onClick: () => openRenameModal(n.id, n.name) },
                    { key: 'copy', icon: <CopyOutlined />, label: '复制到...', onClick: () => openCopyModal(n.id, n.name) },
                    // 「复制场景内容到...」= 把源集下的 case 同时挂到其他场景集（多对多关系）
                    // 与「复制到...」区别：这个不产生新节点，只增加 scene_tree_node 关联行
                    { key: 'copy-content', icon: <CopyOutlined />, label: '复制场景内容到...', onClick: () => openCopyContentModal(n.id, n.name) },
                    ...(canManageSecurity ? [
                      { type: 'divider' as const },
                      (n.securityLevel ?? 0) > 0
                        ? { key: 'unsecure', icon: <UnlockOutlined />, label: '取消保密', onClick: () => handleToggleNodeSecurity(n) }
                        : { key: 'secure', icon: <LockFilled />, label: '设为保密', onClick: () => handleToggleNodeSecurity(n) },
                    ] : []),
                    { key: 'delete', icon: <DeleteOutlined />, label: '删除', danger: true, onClick: () => handleDelete(n.id, n.name) },
                  ],
                }}
              >
                <Tooltip title={effectiveLocked ? lockedTip : ''}>
                  <MoreOutlined
                    style={{
                      padding: '0 4px',
                      cursor: effectiveLocked ? 'not-allowed' : 'pointer',
                      color: effectiveLocked ? '#d9d9d9' : '#999',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </Dropdown>
            </div>
          ),
          children: n.children?.length
            ? convert(n.children, effectiveLocked, nowInsideDaily)
            : groupChildren,
          isLeaf: isLeafForTree,
        };
      });
    return convert(treeData, false, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData, dailyRootId, groupStatsByLeaf, treeSearchKw]);

  // 表格列宽可拖拽:与 JobList / DailySimulation 沿用同一套 ResizableTitle,
  // 用户拖拽后的宽度存 useState (跨页保留、离开页面重置)。key 与列 dataIndex 对齐。
  const { columnWidths, resizeHeaderCell } = useResizableColumns({
    id: 80,
    name: 220,
    apiCode: 140,
    label: 120,
    source: 110,
    creator: 100,
    createTime: 180,
    updateTime: 180,
    mapName: 120,
    mapVersion: 100,
    sceneInfo: 160,
    previewUrl: 160,
    actions: 72,
  });

  const columns = [
    { title: 'ID', dataIndex: 'id', width: columnWidths.id, onHeaderCell: resizeHeaderCell('id') },
    {
      title: '场景名称',
      dataIndex: 'name',
      width: columnWidths.name,
      onHeaderCell: resizeHeaderCell('name'),
      ellipsis: true,
      render: (name: string, record: SceneListItem) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Link
            to={`/scene-annotate?sceneId=${record.id}&nodeId=${selectedNodeId ?? ''}&page=${pagination.current}&from=management`}
            style={{ color: '#5e6ad2', cursor: 'pointer' }}
          >
            {name}
          </Link>
          {(record.securityLevel ?? 0) > 0 && (
            <Tooltip title="保密：仅具备查看权限的角色可见">
              <LockFilled style={{ color: '#b45309' }} />
            </Tooltip>
          )}
          {record.isDeleted ? (
            <Tooltip title="可见但运行任务被过滤">
              <Tag className="scene-disabled-tag" style={{ cursor: 'help' }}>已禁用</Tag>
            </Tooltip>
          ) : null}
        </span>
      ),
    },
    {
      title: 'API_CODE',
      dataIndex: 'apiCode',
      width: columnWidths.apiCode,
      onHeaderCell: resizeHeaderCell('apiCode'),
      ellipsis: true,
      render: (v: string | null | undefined, record: SceneListItem) => (
        <ApiCodeLink
          code={v}
          source={record.source as string | null | undefined}
          region={record.region as string | null | undefined}
        />
      ),
    },
    {
      title: '标签',
      dataIndex: 'label',
      width: columnWidths.label,
      onHeaderCell: resizeHeaderCell('label'),
      ellipsis: true,
      render: (v: string | null | undefined) => v || '-',
    },
    {
      title: '来源',
      dataIndex: 'source',
      width: columnWidths.source,
      onHeaderCell: resizeHeaderCell('source'),
      render: (v: string | null | undefined) => <SceneSourceTag source={v} />,
    },
    { title: '创建人', dataIndex: 'creator', width: columnWidths.creator, onHeaderCell: resizeHeaderCell('creator') },
    { title: '创建时间', dataIndex: 'createTime', width: columnWidths.createTime, onHeaderCell: resizeHeaderCell('createTime'), render: formatTime },
    { title: '更新时间', dataIndex: 'updateTime', width: columnWidths.updateTime, onHeaderCell: resizeHeaderCell('updateTime'), render: formatTime },
    { title: '地图', dataIndex: 'mapName', width: columnWidths.mapName, onHeaderCell: resizeHeaderCell('mapName') },
    { title: '版本', dataIndex: 'mapVersion', width: columnWidths.mapVersion, onHeaderCell: resizeHeaderCell('mapVersion') },
    // 「场景类型」列已下线:后端返回的其实是场景所在树节点的路径 slug
    // (SceneTree.scene_type,形如 "/日常/绕行/环岛_变道"),与左侧树选中的节点
    // 完全冗余,且列名容易被误解为"场景种类";用户真正关心的种类是 sceneGroup。
    {
      // 后端字段名 scene_info，但语义就是用户备注；列名展示为「备注」
      title: '备注',
      dataIndex: 'sceneInfo',
      width: columnWidths.sceneInfo,
      onHeaderCell: resizeHeaderCell('sceneInfo'),
      ellipsis: true,
      render: (v: string | null | undefined) => v || '-',
    },
    {
      // 缩略图列放最后，腾出原 record 地址 + VIN 共 320px 给图。
      // 用 antd <Image> 可一键放大查看；preview 不存在时浏览器 404 → fallback SVG。
      title: '预览',
      dataIndex: 'previewUrl',
      width: columnWidths.previewUrl,
      onHeaderCell: resizeHeaderCell('previewUrl'),
      align: 'center' as const,
      fixed: 'right' as const,
      render: (url: string | null | undefined) => {
        if (!url) {
          return (
            <Tooltip title="尚未生成预览（去后台「场景预览」生成）">
              <div
                style={{
                  width: 128, height: 72, borderRadius: 4, border: '1px dashed #e5e6eb',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  color: '#c9cdd4',
                }}
              >
                <PictureOutlined style={{ fontSize: 22 }} />
              </div>
            </Tooltip>
          );
        }
        return (
          <Image
            src={url}
            width={128}
            height={72}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            placeholder={
              <div
                style={{
                  width: 128, height: 72, background: '#f7f8fa',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Spin size="small" />
              </div>
            }
            fallback="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMjgiIGhlaWdodD0iNzIiIHZpZXdCb3g9IjAgMCAxMjggNzIiPjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iNzIiIGZpbGw9IiNmN2Y4ZmEiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iI2M5Y2RkNCIgZm9udC1zaXplPSIxMiI+5pyq55Sf5oiQ5oiQ6aKE6KeIPC90ZXh0Pjwvc3ZnPg=="
            preview={{
              mask: <span style={{ fontSize: 12 }}>查看大图</span>,
            }}
          />
        );
      },
    },
    // 单个场景的「操作」列：仅对持有 data:confidential:manage 的用户显示；
    // 无权限用户没有可操作项，整列不渲染避免留一列空白。放全表最后一列。
    ...(canManageSecurity ? [{
      title: '操作',
      key: 'actions',
      width: columnWidths.actions,
      onHeaderCell: resizeHeaderCell('actions'),
      align: 'center' as const,
      fixed: 'right' as const,
      onCell: () => ({ style: { overflow: 'visible' } }),
      render: (_: unknown, record: SceneListItem) => {
        const confidential = (record.securityLevel ?? 0) > 0;
        return (
          <Dropdown
            trigger={['click']}
            menu={{
              items: [confidential
                ? {
                    key: 'unsecure',
                    icon: <UnlockOutlined />,
                    label: '取消保密',
                    onClick: () => handleToggleSceneSecurity(record),
                  }
                : {
                    key: 'secure',
                    icon: <LockFilled />,
                    label: '设为保密',
                    onClick: () => handleToggleSceneSecurity(record),
                  },
              ],
            }}
          >
            <Button type="text" size="small" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    }] : []),
  ];

  return (
    // 根 div 用 calc(100dvh - 48px) 锁死到视口高度(减 AppHeader 48px);
    // 不用 height:100% —— 外层 .sidebar-content-inner 是 overflow:auto,
    // height:100% 只让容器"至少和父一样高",子超高时依然滚,导致底部对不齐。
    // 100dvh 直接绑视口,场景列表 flex:1 就能撑满 → 底部与左侧场景树对齐。
    <div style={{
      display: 'flex',
      height: 'calc(100dvh - 48px)',
      padding: 16,
      gap: 16,
      boxSizing: 'border-box',
    }}>
      {/* 左侧树 */}
      <div style={{
        width: 320, minWidth: 280, flexShrink: 0,
        background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid #f0f0f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 8,
        }}>
          <Segmented
            size="small"
            value={leftPanelMode}
            onChange={(v) => setLeftPanelMode(v as 'scene-set' | 'tag')}
            options={[
              { label: '场景集', value: 'scene-set' },
              { label: '标签', value: 'tag' },
            ]}
          />
          {/*
           * 搜索框 + 概览 + ... 按钮组：搜索框放在概览左边;
           * 「根目录」新建入口折进 ... 下拉,常用度低不占顶栏位置。
           */}
          {leftPanelMode === 'scene-set' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0, justifyContent: 'flex-end' }}>
            <Input.Search
              size="small"
              placeholder="搜索场景集"
              value={treeSearchInput}
              onChange={(e) => setTreeSearchInput(e.target.value)}
              // onSearch 触发时机:回车 / 点搜索按钮 / allowClear 清除。
              // 提交后才更新 treeSearchValue,驱动 useEffect 里的展开收起逻辑,
              // 避免边打字边整树 walk。
              onSearch={(v) => setTreeSearchValue(v.trim())}
              allowClear
              enterButton={<SearchOutlined />}
              style={{ flex: 1, minWidth: 0, maxWidth: 200 }}
            />
            {/*
             * 「概览」按钮：右侧切回 SceneOverviewPanel 并清空 Tree 选中。
             * 默认进入页面（viewMode==='overview' && 无选中节点）就是这个状态，
             * 按钮主要服务于「列表 → 概览」回切（替代独立菜单项）。
             */}
            <Button
              size="small"
              icon={<DashboardOutlined />}
              type={viewMode === 'overview' ? 'primary' : 'default'}
              onClick={handleSwitchToOverview}
            >
              概览
            </Button>
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  {
                    key: 'add-root',
                    icon: <FolderAddOutlined />,
                    label: '新建根目录',
                    onClick: () => openCreateModal(-1, 0),
                  },
                  { type: 'divider' as const },
                  {
                    key: 'expand-all',
                    icon: <PlusOutlined />,
                    label: '展开所有集合',
                    onClick: handleExpandAll,
                  },
                  {
                    key: 'collapse-all',
                    icon: <ClearOutlined />,
                    label: '关闭所有集合',
                    onClick: handleCollapseAll,
                  },
                ],
              }}
            >
              <Button size="small" icon={<MoreOutlined />} />
            </Dropdown>
          </div>
          )}
        </div>
        {/*
         * 场景树勾选操作栏：真实集合 + 分组虚拟节点勾选统一走 sceneSetIds 语义
         * （后端 job_service._parse_scene_set_tokens 解析 L<leafId>G:<groupKey> 复合 token），
         * 不受 sceneIds 100 上限约束。
         */}
        {leftPanelMode === 'scene-set' && checkedKeys.length > 0 && (
          <div style={{
            padding: '8px 12px', borderBottom: '1px solid #f0f0f0',
            background: '#fafbff',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
          }}>
            <div style={{ fontSize: 12, color: '#4b5563', minWidth: 0, flex: 1 }}>
              已勾选 <b>{checkedKeys.length}</b> 个节点
            </div>
            <Space size={4}>
              <Button
                size="small"
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={() => setJobModalOpen(true)}
              >
                发起任务
              </Button>
              <Button size="small" onClick={() => setCheckedKeys([])}>
                清空
              </Button>
            </Space>
          </div>
        )}
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
          {leftPanelMode === 'tag' ? (
            tagTreeLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            ) : (
              <Tree
                treeData={tagAntdData}
                // 与「场景集」面板一致的复选框多选 + 父子级联：勾母节点自动勾全部子节点，
                // 取消子节点母节点变为半选。后台接口取并集展开目录，不依赖前端的分子结构。
                checkable
                checkedKeys={checkedTagKeys}
                expandedKeys={tagExpandedKeys}
                onExpand={(keys) => setTagExpandedKeys(keys)}
                onCheck={(keys) => {
                  // 级联模式下 keys 为 {checked, halfChecked}；防御回调两种形态。
                  const arr = Array.isArray(keys) ? keys : keys.checked;
                  handleTagCheck(arr);
                }}
                blockNode
                showLine={{ showLeafIcon: false }}
                style={{ padding: '0 8px' }}
              />
            )
          ) : treeLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
          ) : treeData.length === 0 ? (
            <Empty description="暂无场景集" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '40px 0' }} />
          ) : (
            <Tree
              treeData={antTreeData}
              // 选中分组虚拟节点时高亮虚拟 key 而非真实叶子 id；未选中分组时仍高亮真实叶子。
              selectedKeys={
                selectedGroupNode
                  ? [encodeGroupKey(selectedGroupNode.parentLeafId, selectedGroupNode.groupKey)]
                  : (selectedNodeId !== null ? [selectedNodeId] : [])
              }
              expandedKeys={expandedKeys}
              onExpand={handleTreeExpand}
              onSelect={handleSelect}
              // 目录 + 叶子 + daily 分组虚拟节点都能勾；antd 自带级联（勾父=选中全部子）。
              checkable
              checkedKeys={checkedKeys}
              onCheck={(keys) => {
                // antd 未启用 strictly 时返 Key[]；启用了才返 {checked, halfChecked}。这里防御两种形态。
                const arr = Array.isArray(keys) ? keys : keys.checked;
                setCheckedKeys(arr);
              }}
              blockNode
              showLine={{ showLeafIcon: false }}
              style={{ padding: '0 8px' }}
            />
          )}
        </div>
      </div>

      {/* 右侧主区域：viewMode 决定渲染概览面板还是场景列表 */}
      <div style={{
        flex: 1, background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {leftPanelMode === 'tag' ? (
          // 标签检索模式：右侧展示所选标签（含目录，取子标签并集）下的场景列表。
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div style={{
              padding: '12px 16px', borderBottom: '1px solid #f0f0f0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <Text strong style={{ fontSize: 15 }}>
                    {selectedTags.length > 0 ? '标签组合场景' : '标签场景'}
                  </Text>
                  {selectedTags.map((t) => (
                    <Tag
                      key={t.id}
                      closable
                      color="processing"
                      onClose={() => handleTagRemove(t.id)}
                      style={{ marginInlineEnd: 0 }}
                    >
                      {tagDisplayName(t)}
                    </Tag>
                  ))}
                </div>
                <span style={{ fontSize: 12, color: '#8b95a5' }}>
                  {selectedTags.length === 0
                    ? '在左侧勾选一个或多个标签查看场景'
                    : `按标签交集 · 共 ${tagPagination.total} 个场景`}
                </span>
              </div>
              <Space>
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  disabled={checkedTagKeys.length === 0 || tagPagination.total === 0}
                  onClick={() => setJobModalOpen(true)}
                >
                  发起任务（{tagPagination.total}）
                </Button>
                {checkedTagKeys.length > 0 && (
                  <Button size="small" onClick={clearTagSelection}>
                    清空选择
                  </Button>
                )}
              </Space>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
              <Table
                rowKey="id"
                size="small"
                loading={tagScenesLoading}
                dataSource={tagScenes}
                columns={TAG_SCENE_COLUMNS}
                pagination={{
                  current: tagPagination.current,
                  pageSize: tagPagination.pageSize,
                  total: tagPagination.total,
                  showSizeChanger: false,
                  showTotal: (t) => `共 ${t} 个场景`,
                  onChange: (page, pageSize) => {
                    if (maximalCheckedTags.length > 0) {
                      void loadTagScenes(maximalCheckedTags.map((t) => t.id), page, pageSize);
                    }
                  },
                }}
              />
            </div>
          </div>
        ) : viewMode === 'overview' ? (
          // 默认视图 = 紧凑概览 + 全量场景列表。概览提供总体统计与趋势;下方
          // 全量列表复用 SceneGeneration 抽出来的 <FullSceneList>,与选中场景集
          // 的列表在 UI 上区分:全量列表侧重多维筛选 + 批量操作,选中集合走列表
          // 分支专注该集合内的场景增删。
          // 布局:外层 flex-column + 概览按内容高度、列表 flex:1 撑满 →
          // 列表底部与左侧场景树对齐,不留大片空白。
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <SceneOverviewPanel />
            {/* 场景列表区:flex:1 抢占概览之外的所有剩余空间,内部再分标题栏和列表主体 */}
            <div style={{
              flex: 1,
              minHeight: 0,
              display: 'flex', flexDirection: 'column',
              padding: '16px 16px 16px 16px',
              marginTop: 8,
              borderTop: '1px solid #f0f0f0',
              overflow: 'hidden',
            }}>
              {/* 明确列表分节标题,让用户一眼看到"下方这块是场景列表",
                  避免概览下面直接冒出筛选栏时不知道是什么。 */}
              <div style={{
                fontSize: 15,
                fontWeight: 600,
                color: '#1d1d1f',
                marginBottom: 12,
                flex: '0 0 auto',
              }}>
                场景列表
              </div>
              {/* FullSceneList 内部已 flex-column + 表格内滚,外面不用再包 overflow */}
              <div style={{ flex: 1, minHeight: 0 }}>
                <FullSceneList
                  sceneTree={treeData}
                  fromKey="management"
                  showCommentColumn={false}
                  syncUrl={false}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
        {/* 选中场景集/目录时,顶部复用同一概览面板,scope 到该节点(递归含后代)。
            未选中时(初始进入 scene-list 视图理论不会,但兜底跳过面板)。 */}
        {selectedNodeId !== null && (
          <div style={{ borderBottom: '1px solid #f0f0f0' }}>
            <SceneOverviewPanel treeNodeId={selectedNodeId} />
          </div>
        )}
        {/* 标题栏 */}
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid #f0f0f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <Text strong style={{ whiteSpace: 'nowrap' }}>
              {selectedNode ? (
                <>
                  {selectedNode.name}
                  {/* 带上树节点 ID，便于对照工单 / 接口排查；弱化展示不抢名称层级 */}
                  <span style={{ color: '#8c8c8c', fontWeight: 400, fontSize: 13 }}>
                    （ID: {selectedNode.id}）
                  </span>
                  {' - 场景列表'}
                </>
              ) : '请选择左侧场景集节点'}
            </Text>
            {selectedNode && (
              <Text type="secondary">共 {pagination.total} 条</Text>
            )}
            {/*
             * 创建人 / 创建时间元信息：从树节点行迁移到此处，
             * 仅展示当前选中节点一份，避免树里每行一个色块的视觉拥挤。
             * hover 出绝对时间（兜底 24h+ 也能精确读到具体日期）。
             */}
            {selectedNode?.creator && (
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                创建人 <span style={{ color: '#4b5563' }}>{selectedNode.creator}</span>
                {selectedNode.createTime && (
                  <>
                    {' · 创建于 '}
                    <Tooltip title={formatTime(selectedNode.createTime)}>
                      <span style={{ color: '#4b5563', cursor: 'help' }}>
                        {getRelativeTime(selectedNode.createTime)}
                      </span>
                    </Tooltip>
                  </>
                )}
              </Text>
            )}
          </div>
          {isLeafNode && (
            <Space>
              {selectedLocked && (
                <Tooltip title="该场景集（或其祖先目录）被锁定，所有写入操作禁用">
                  <Tag color="orange" icon={<LockOutlined />}>已锁定</Tag>
                </Tooltip>
              )}
              <Tooltip
                title={
                  selectedRowKeys.length > MAX_SCENE_IDS_PER_JOB
                    ? `最多按 ${MAX_SCENE_IDS_PER_JOB} 个场景发起，当前已选 ${selectedRowKeys.length} 个`
                    : '按勾选的场景发起仿真 Job'
                }
              >
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  disabled={!selectedRowKeys.length || selectedRowKeys.length > MAX_SCENE_IDS_PER_JOB}
                  onClick={() => setJobModalOpen(true)}
                >
                  发起任务 {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ''}
                </Button>
              </Tooltip>
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={!selectedRowKeys.length || selectedLocked}
                onClick={handleBatchDelete}
              >
                删除场景 {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ''}
              </Button>
              <Button
                danger
                icon={<ClearOutlined />}
                disabled={!pagination.total || selectedLocked}
                onClick={handleClearScenes}
              >
                清空场景集 {pagination.total > 0 ? `(${pagination.total})` : ''}
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={selectedLocked}
                onClick={() => navigate(`/add-scenes-to-tree/${selectedNodeId}?name=${encodeURIComponent(selectedNode?.name || '')}`)}
              >
                添加场景
              </Button>
            </Space>
          )}
        </div>

        {/* 查询筛选栏（4 项均支持多值；ID/Label 走 IN，名称/Jira 走 OR LIKE） */}
        {selectedNodeId !== null && (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
            <Space wrap>
              {/* 三个 tags 框:输入框有内容时 Enter 转标签,清空后 Enter 直接查询。 */}
              <Select
                mode="tags"
                placeholder="场景 ID（可多个，回车确认）"
                value={filterIds}
                onChange={(vals: string[]) => setFilterIds(
                  vals.map(v => v.trim()).filter(v => v && /^\d+$/.test(v)),
                )}
                {...enterToSearch(1)}
                tokenSeparators={[',', ' ', '，', ';', '；']}
                style={{ minWidth: 220 }}
              />
              <Select
                mode="tags"
                placeholder="场景名称（可多个，回车确认）"
                value={filterNames}
                onChange={(vals: string[]) => setFilterNames(vals.map(v => v.trim()).filter(Boolean))}
                {...enterToSearch(2)}
                tokenSeparators={[',', ' ', '，', ';', '；']}
                style={{ minWidth: 240 }}
              />
              <Select
                mode="tags"
                placeholder="Jira / Issue（可多个，回车确认）"
                value={filterJiras}
                onChange={(vals: string[]) => setFilterJiras(vals.map(v => v.trim()).filter(Boolean))}
                {...enterToSearch(3)}
                tokenSeparators={[',', ' ', '，', ';', '；']}
                style={{ minWidth: 240 }}
              />
              {/* 两个多选框:回车即查询(下拉展开时先让位给选中)。
                  选项在页面挂载时已预加载,不需要展开时再懒加载。 */}
              <Select
                mode="multiple"
                placeholder="标签（可多选）"
                value={filterLabels}
                onChange={(vals: string[]) => setFilterLabels(vals)}
                {...enterToSearch(4)}
                options={labelOptions.map(l => ({ value: l, label: l }))}
                loading={labelLoading}
                allowClear
                showSearch
                maxTagCount="responsive"
                style={{ minWidth: 220 }}
              />
              <Select
                mode="multiple"
                placeholder="来源（可多选）"
                value={filterSources}
                onChange={(vals: string[]) => setFilterSources(vals)}
                {...enterToSearch(5)}
                options={SCENE_SOURCE_FILTER_OPTIONS}
                allowClear
                maxTagCount="responsive"
                style={{ minWidth: 180 }}
              />
              <Select
                mode="multiple"
                placeholder="区域（可多选）"
                value={filterRegions}
                onChange={(vals: string[]) => setFilterRegions(vals)}
                {...enterToSearch(6)}
                options={[
                  { value: 'cn', label: '国内' },
                  { value: 'ae', label: '阿联酋' },
                  { value: 'sea', label: '新加坡' },
                ]}
                allowClear
                maxTagCount="responsive"
                style={{ minWidth: 160 }}
              />
              <DatePicker.RangePicker
                showTime={{ format: 'HH:mm:ss' }}
                format="YYYY-MM-DD HH:mm:ss"
                placeholder={['创建开始时间', '创建结束时间']}
                value={filterCreateTimeRange}
                onChange={(range) => {
                  if (!range || !range[0] || !range[1]) {
                    setFilterCreateTimeRange(null);
                  } else {
                    setFilterCreateTimeRange([range[0], range[1]]);
                  }
                }}
                style={{ minWidth: 380 }}
                allowClear
              />
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              <Button
                onClick={() => {
                  setFilterIds([]);
                  setFilterNames([]);
                  setFilterJiras([]);
                  setFilterLabels([]);
                  setFilterSources([]);
                  setFilterRegions([]);
                  setFilterCreateTimeRange(null);
                  if (selectedNodeId !== null) {
                    loadScenes(selectedNodeId, 1, pagination.pageSize, {
                      ids: [], names: [], jiras: [], labels: [], sources: [], regions: [],
                      createTimeRange: null,
                    });
                  }
                }}
              >
                重置
              </Button>
            </Space>
          </div>
        )}

        {/* 注意:sticky 表头以 padding-box 内边为 top:0 参考,scroll 容器不给
            padding,padding 留在 Empty / 内部结构上单独控。 */}
        <div ref={sceneTableScrollRef} style={{ flex: 1, overflow: 'auto' }}>
          {selectedNodeId === null ? (
            <Empty description="点击左侧树节点查看场景" style={{ marginTop: 80 }} />
          ) : (
            <Table
              rowKey="id"
              columns={columns}
              dataSource={scenes}
              loading={tableLoading}
              size="small"
              // 列宽拖拽:与 JobList / DailySimulation 同一套 ResizableTitle;
              // scroll.x 用列宽总和,拖大列后横滚区自动放宽。
              components={{ header: { cell: ResizableTitle } }}
              tableLayout="fixed"
              scroll={{ x: Object.values(columnWidths).reduce((a, b) => a + b, 0) }}
              // 表头固定:sticky.getContainer 指向外层 overflow: auto 容器,与
              // FullSceneList / AddSceneToTree 同源规约,让表头在容器内滚时钉在顶部。
              sticky={{ getContainer: () => sceneTableScrollRef.current ?? window }}
              // 全局规则：场景列表展示路径不过滤已禁用场景，按 isDeleted 置灰；
              // 全局规则见 db-changes §C.1。
              rowClassName={(r) => (r.isDeleted ? 'scene-row-disabled' : '')}
              rowSelection={isLeafNode ? {
                selectedRowKeys,
                preserveSelectedRowKeys: true,
                onChange: (keys, rows) => {
                  setSelectedRowKeys(keys);
                  // 跨页累积：保留 prev 中仍然选中的，再合入当前页选中的 rows（按 id 去重）
                  setSelectedScenes((prev) => {
                    const keySet = new Set(keys.map(Number));
                    const map = new Map<number, PresetScene>();
                    for (const s of prev) {
                      if (keySet.has(s.id)) map.set(s.id, s);
                    }
                    for (const r of rows) {
                      if (keySet.has(r.id)) map.set(r.id, { id: r.id, name: r.name });
                    }
                    return [...map.values()];
                  });
                },
              } : undefined}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100', '1000'],
                showTotal: t => `共 ${t} 条`,
                onChange: (page, size) => loadScenes(selectedNodeId!, page, size, null, selectedGroupNode),
              }}
            />
          )}
        </div>
          </>
        )}
      </div>

      {/* 新建目录/场景集 Modal */}
      <Modal
        title={createNodeFlag === 0 ? '新建目录' : '新建场景集'}
        open={createModalOpen}
        onOk={handleCreateDir}
        onCancel={() => setCreateModalOpen(false)}
        destroyOnHidden
      >
        <Input
          placeholder={createNodeFlag === 0 ? '请输入目录名称' : '请输入场景集名称'}
          value={createName}
          onChange={e => setCreateName(e.target.value)}
          onPressEnter={handleCreateDir}
          autoFocus
        />
      </Modal>

      {/* 重命名 Modal */}
      <Modal
        title="重命名"
        open={renameModalOpen}
        onOk={handleRename}
        onCancel={() => setRenameModalOpen(false)}
        destroyOnHidden
      >
        <Input
          placeholder="请输入新名称"
          value={renameName}
          onChange={e => setRenameName(e.target.value)}
          onPressEnter={handleRename}
          autoFocus
        />
      </Modal>

      {/* 场景集合复制 Modal（浅拷贝节点 + scene_tree_node 关联，支持目录递归） */}
      <Modal
        title="复制到..."
        open={copyModalOpen}
        onOk={handleCopy}
        onCancel={() => setCopyModalOpen(false)}
        confirmLoading={copySubmitting}
        okText="复制"
        destroyOnHidden
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 6 }}>目标父节点</div>
          <TreeSelect
            value={copyTargetParentId}
            treeData={copyTargetTreeData}
            onChange={(v: number) => setCopyTargetParentId(v)}
            treeDefaultExpandAll
            style={{ width: '100%' }}
            placeholder="请选择目标父节点"
            popupMatchSelectWidth={false}
          />
        </div>
        <div>
          <div style={{ marginBottom: 6 }}>新名称</div>
          <Input
            placeholder="请输入新名称"
            value={copyNewName}
            onChange={e => setCopyNewName(e.target.value)}
            onPressEnter={handleCopy}
            maxLength={255}
          />
        </div>
        <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
          复制规则：仅复制节点结构与场景关联，不复制场景实体；目录将连同所有子节点一起复制。
        </Text>
      </Modal>

      {/* 「复制场景内容到...」Modal —— 跨集追加多对多关联（不产生新节点）
          与「复制到...」（浅拷贝节点）语义完全不同，独立 Modal 避免混淆。 */}
      <Modal
        title={`复制场景内容到... · 源：${copyContentSourceName}`}
        open={copyContentModalOpen}
        onOk={handleCopyContent}
        onCancel={() => setCopyContentModalOpen(false)}
        confirmLoading={copyContentSubmitting}
        okText="确认复制"
        okButtonProps={{
          disabled: copyContentTargetIds.length === 0 || copyContentPreviewTotal === 0,
        }}
        destroyOnHidden
        width={560}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 6 }}>
            <Text strong>目标场景集</Text>
            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
              可多选，最多 20 个；只能选叶子（场景集），目录不可选
            </Text>
          </div>
          <TreeSelect
            treeData={copyContentTargetTreeData}
            value={copyContentTargetIds}
            onChange={(v: number[]) => setCopyContentTargetIds(v)}
            multiple
            treeCheckable
            showCheckedStrategy="SHOW_ALL"
            treeDefaultExpandAll={false}
            style={{ width: '100%' }}
            placeholder="选择一个或多个目标场景集"
            popupMatchSelectWidth={false}
            maxTagCount={5}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 6 }}>
            <Text strong>筛选条件</Text>
            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
              可选，只复制满足条件的 case
            </Text>
          </div>
          <Checkbox.Group
            value={copyContentStatusFilter}
            onChange={(v) => setCopyContentStatusFilter(v as number[])}
            // options 由 /scene/status 驱动(SceneStatusEnum);客户端过滤掉 5=质检中
            // (进行中,产物未定型不适合被复制);其余稳态全放开,包括
            // 7=已完成-瑕疵品(下游行为等同 8=已完成,可入集、可发任务)。
            options={copyContentStatusOptions}
          />
        </div>
        <div style={{
          padding: '8px 12px', background: '#fafafa',
          borderRadius: 6, border: '1px solid #f0f0f0', marginBottom: 8,
        }}>
          {copyContentPreviewTotal === -1
            ? <Text type="secondary">正在预览…</Text>
            : copyContentPreviewTotal === null
              ? <Text type="secondary">预览未加载</Text>
              : (
                <span>
                  <Text>源集共有</Text>
                  <Text strong style={{ margin: '0 4px', color: '#1677ff' }}>
                    {copyContentPreviewTotal}
                  </Text>
                  <Text>个满足条件的 case，将追加到 {copyContentTargetIds.length} 个目标场景集</Text>
                </span>
              )}
        </div>
        {copyContentResult && (
          <div style={{ marginTop: 8, fontSize: 12 }}>
            <Text strong>本次结果：</Text>
            {copyContentResult.targets.map(t => (
              <div key={t.nodeId} style={{ marginTop: 4 }}>
                <Tag color="blue">{t.name || `#${t.nodeId}`}</Tag>
                <Text type="secondary">
                  新增 <Text strong style={{ color: '#52c41a' }}>{t.added}</Text>
                  {t.skipped > 0 && <>，跳过已存在 <Text strong>{t.skipped}</Text></>}
                </Text>
              </div>
            ))}
          </div>
        )}
        <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
          规则：同一 case 可同时属于多个场景集；目标里已有的 case 会自动跳过。
        </Text>
      </Modal>

      {/* 清空场景集 Modal（高危：必须输入完整集合名称才能确认） */}
      <Modal
        title="清空场景集"
        open={clearModalOpen}
        onOk={submitClearScenes}
        onCancel={() => setClearModalOpen(false)}
        confirmLoading={clearSubmitting}
        okButtonProps={{
          danger: true,
          disabled: !clearTargetNode || clearConfirmText.trim() !== clearTargetNode.name,
        }}
        okText="清空"
        destroyOnHidden
      >
        {clearTargetNode && (
          <>
            <div style={{ marginBottom: 12 }}>
              此操作将清空场景集
              <Text strong style={{ margin: '0 4px' }}>「{clearTargetNode.name}」</Text>
              下的全部
              <Text strong style={{ margin: '0 4px' }}>{clearTargetNode.total}</Text>
              个场景关联。
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
              仅移除场景与该场景集的关联，不会删除场景本身，也不会删除场景集节点。
            </Text>
            <div style={{ marginBottom: 6 }}>
              请输入场景集名称
              <Text code style={{ margin: '0 4px' }}>{clearTargetNode.name}</Text>
              以确认清空：
            </div>
            <Input
              placeholder="输入场景集名称"
              value={clearConfirmText}
              onChange={e => setClearConfirmText(e.target.value)}
              onPressEnter={submitClearScenes}
              autoFocus
            />
          </>
        )}
      </Modal>

      {/*
       * 发起 Job 弹窗：两条触发路径。
       * - 树上勾选（含真实集合 + daily 分组虚拟节点）：encodedPresetSceneSetIds
       *   合并进 sceneSetIds 逗号串（后端 job_service._parse_scene_set_tokens 解析
       *   L<leafId>G:<groupKey> 复合 token），走「按场景集」链路，不受 100 上限
       * - 表格勾选具体场景：selectedScenes → presetScenes（sceneIds 语义，100 上限）
       */}
      <CreateJobModal
        open={jobModalOpen}
        presetSceneSetIds={leftPanelMode === 'scene-set' && checkedKeys.length > 0 ? encodedPresetSceneSetIds : undefined}
        presetSceneSetGroupLabels={leftPanelMode === 'scene-set' && checkedKeys.length > 0 ? presetSceneSetGroupLabels : undefined}
        presetScenes={leftPanelMode === 'scene-set' && checkedKeys.length > 0 ? undefined : (leftPanelMode === 'scene-set' ? selectedScenes : undefined)}
        presetSceneTagIds={leftPanelMode === 'tag' && maximalCheckedTags.length > 0 ? maximalCheckedTags.map((t) => t.id) : undefined}
        presetSceneTagLabels={
          leftPanelMode === 'tag' && maximalCheckedTags.length > 0
            ? Object.fromEntries(maximalCheckedTags.map((t) => [
              t.id,
              // 目录组展示其叶子标签名(与场景管理 chips 一致),不展示目录名
              collectLeavesOf(t).map((l) => tagDisplayName(l)).join(' / ') || tagDisplayName(t),
            ]))
            : undefined
        }
        onClose={() => setJobModalOpen(false)}
        onSuccess={() => {
          setJobModalOpen(false);
          clearSelection();
          setCheckedKeys([]);
        }}
      />

    </div>
  );
}
